<template>
  <v-card flat outlined class="competitors-card">
    <!-- Section Header -->
    <v-card-title class="py-3 competitors-header">
      <v-icon left color="primary" small>mdi-store-outline</v-icon>
      <span class="text-subtitle-2 font-weight-medium">Competitors Pricing</span>
      <v-spacer></v-spacer>
      <v-chip x-small color="primary" text-color="white" v-if="competitors.length">
        {{ competitors.length }} competitor{{ competitors.length !== 1 ? 's' : '' }}
      </v-chip>
    </v-card-title>

    <v-card-text class="pa-0">
      <v-simple-table class="competitors-table">
        <template v-slot:default>
          <thead>
            <tr class="table-header-row">
              <th class="px-4 py-3 text-subtitle-2">Competitor</th>
              <th class="px-4 py-3 text-subtitle-2">Product</th>
              <th class="px-4 py-3 text-subtitle-2" v-if="is_similarity_account == 'true'">
                Similarity
              </th>
              <th class="px-4 py-3 text-subtitle-2">Price</th>
              <th class="px-4 py-3 text-subtitle-2 text-center">Stock</th>
              <th class="px-4 py-3" style="width: 40px"></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(competitor, index) in competitors"
              :key="`${competitor.website.id}_${index}`"
              class="competitor-row"
              :class="[
                competitor.product.not_found ? 'not-found-row' : '',
                index === 0 ? 'own-product-row' : '',
                !competitor.product.validated && index !== 0 ? 'unvalidated-row' : '',
              ]"
              @click="!competitor.product.validated && index !== 0 ? openValidateModal() : null"
              :style="!competitor.product.validated && index !== 0 ? 'cursor: pointer' : ''"
            >
              <!-- Competitor -->
              <td class="px-4 py-3">
                <div class="d-flex align-center">
                  <div class="favicon-wrapper mr-2">
                    <img
                      :src="getWebsiteFavicon(competitor.website.url)"
                      :alt="competitor.website.alias"
                      height="22"
                      width="22"
                      class="favicon-img"
                    />
                  </div>
                  <v-icon class="mr-2" size="10" :color="competitor.website.config.color"
                    >$circle</v-icon
                  >
                  <a
                    :href="competitor.product.url"
                    target="_blank"
                    class="competitor-link text-body-2 font-weight-medium"
                  >
                    {{ competitor.website.alias }}
                  </a>
                  <img
                    v-if="competitor.product.isGoogleShoppingProduct"
                    :src="require(`../../assets/google_shopping_favicon.png`)"
                    width="14"
                    height="14"
                    class="ml-2 favicon-img"
                  />
                </div>
              </td>

              <!-- Product Name -->
              <td class="px-4 py-3">
                <v-tooltip bottom max-width="400">
                  <template v-slot:activator="{ on, attrs }">
                    <div v-bind="attrs" v-on="on" class="product-name-cell">
                      <span class="text-body-2">{{ truncate(competitor.product.name, 90) }}</span>
                      <div v-if="competitor.product.meta?.specifications" class="mt-1">
                        <template v-for="spec in competitor.product.meta.specifications">
                          <v-chip
                            v-if="spec.value !== 'unknown'"
                            :key="spec.name"
                            x-small
                            outlined
                            color="grey"
                            class="mr-1 mb-1 spec-chip"
                          >
                            {{ spec.value }}
                          </v-chip>
                        </template>
                      </div>
                    </div>
                  </template>
                  <span>{{ competitor.product.name }}</span>
                </v-tooltip>
              </td>

              <!-- Similarity -->
              <td class="px-4 py-3" v-if="is_similarity_account == 'true'">
                <v-chip
                  x-small
                  :color="getSimilarityColor(competitor.product.similarity_score)"
                  text-color="white"
                >
                  {{ (competitor.product.similarity_score * 100).toFixed(1) }}%
                </v-chip>
              </td>

              <!-- Price -->
              <td class="px-4 py-3">
                <div class="price-cell">
                  <div
                    class="font-weight-bold"
                    v-html="
                      priceFormatter(
                        competitor,
                        product_match.hight_price,
                        product_match.low_price,
                        true,
                        true,
                      )
                    "
                  ></div>
                  <div
                    class="text-caption text--secondary mt-1"
                    v-if="competitor.product.offers && competitor.product.offers.length"
                  >
                    <v-icon x-small color="grey" class="mr-1">mdi-clock-outline</v-icon>
                    {{ competitor.product.offers[0].updateDate }}
                  </div>
                </div>
              </td>

              <!-- Stock -->
              <td class="text-center px-4 py-3">
                <div
                  class="stock-indicator"
                  :class="'stock--' + getStockStatus(competitor.product.offers[0].availability).key"
                >
                  <v-icon x-small class="stock-icon mr-1">{{
                    getStockStatus(competitor.product.offers[0].availability).icon
                  }}</v-icon>
                  <span class="stock-label text-caption font-weight-medium">{{
                    getStockStatus(competitor.product.offers[0].availability).label
                  }}</span>
                </div>
              </td>

              <!-- Validated -->
              <td class="px-2 py-3 text-center">
                <v-tooltip bottom v-if="competitor.product.validated">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon v-bind="attrs" v-on="on" color="success" small
                      >mdi-check-decagram</v-icon
                    >
                  </template>
                  <span>Validated match</span>
                </v-tooltip>
                <v-tooltip bottom v-else>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon v-bind="attrs" v-on="on" color="warning" small
                      >mdi-alert-decagram-outline</v-icon
                    >
                  </template>
                  <span>Not validated yet</span>
                </v-tooltip>
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>

      <!-- Empty state -->
      <div v-if="!competitors.length" class="text-center py-8">
        <v-icon size="48" color="grey lighten-2">mdi-store-off-outline</v-icon>
        <div class="text-body-2 grey--text mt-2">No competitor data available</div>
      </div>
    </v-card-text>
  </v-card>
</template>
<script>
  export default {
    props: {
      product_match: Object,
      hide_google_shopping: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        is_similarity_account: localStorage.getItem('is_similarity_account'),
        competitors: [],
      }
    },
    async mounted() {
      this.initialisation()
    },
    methods: {
      truncate(str, size) {
        if (str.length <= size) {
          return str
        }
        return str.slice(0, size) + '...'
      },
      getSimilarityColor(score) {
        const pct = score * 100
        if (pct >= 80) return 'success'
        if (pct >= 50) return 'warning'
        return 'error'
      },
      getStockStatus(availability) {
        switch (availability) {
          case 'InStock':
            return { key: 'in', label: 'In Stock', icon: 'mdi-check-circle-outline' }
          case 'OutOfStock':
            return { key: 'out', label: 'Out of Stock', icon: 'mdi-close-circle-outline' }
          case 'NotFound':
            return { key: 'notfound', label: 'Not Found', icon: 'mdi-help-circle-outline' }
          default:
            return {
              key: 'out',
              label: availability || 'Unknown',
              icon: 'mdi-alert-circle-outline',
            }
        }
      },
      openValidateModal() {
        this.$emit('show-validate-modal')
      },
      initialisation() {
        if (!this.product_match) return
        this.competitors = []
        Object.keys(this.product_match).forEach((key) => {
          if (this.product_match[key] && this.product_match[key].product) {
            if (this.hide_google_shopping && this.product_match[key].product.isGoogleShoppingProduct)
              return
            if (this.product_match[key].website.alias == localStorage.getItem('website_alias'))
              this.competitors.unshift(this.product_match[key])
            else this.competitors.push(this.product_match[key])
          }

          //order competitors by similarity score
          this.competitors = this.competitors.sort((a, b) => {
            return b.product.similarity_score - a.product.similarity_score
          })
        })
      },
    },
    watch: {
      product_match: {
        deep: true,
        handler() {
          this.initialisation()
        },
      },
      hide_google_shopping() {
        this.initialisation()
      },
    },
  }
</script>
<style scoped>
  /* Card wrapper */
  .competitors-card {
    border-radius: 8px;
    overflow: hidden;
  }

  .competitors-header {
    border-bottom: 1px solid #f0f0f0;
  }

  /* Table styling */
  .competitors-table {
    width: 100%;
  }

  .table-header-row th {
    background: transparent !important;
    font-weight: 600 !important;
    color: rgba(0, 0, 0, 0.45) !important;
    font-size: 0.75rem !important;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 1px solid #eeeeee !important;
  }

  /* Row styling */
  .competitor-row {
    transition: background-color 0.2s ease;
    border-bottom: 1px solid #f8f8f8;
  }

  .competitor-row:hover {
    background-color: #f8f9fe !important;
  }

  .competitor-row:last-child {
    border-bottom: none;
  }

  /* Highlight own product row */
  .own-product-row {
    background-color: #f3f8ff !important;
    border-left: 3px solid var(--v-primary-base, #1976d2);
  }

  .own-product-row:hover {
    background-color: #e8f0fe !important;
  }

  /* Not found row */
  .not-found-row {
    opacity: 0.5;
    background-color: transparent;
  }

  /* Unvalidated row */
  .unvalidated-row {
    border-left: 3px solid #ffa726;
    background-color: #fff8f0;
  }

  .unvalidated-row td {
    filter: blur(1.5px);
    opacity: 0.7;
    transition: filter 0.2s ease, opacity 0.2s ease;
  }

  .unvalidated-row:hover td {
    filter: blur(0px);
    opacity: 1;
  }

  .unvalidated-row:hover {
    background-color: #fff8f0 !important;
  }

  /* Favicon */
  .favicon-wrapper {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    background: transparent;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .favicon-img {
    border-radius: 4px;
    object-fit: contain;
  }

  /* Competitor link */
  .competitor-link {
    text-decoration: none;
    color: inherit;
    white-space: nowrap;
  }

  .competitor-link:hover {
    color: var(--v-primary-base, #1976d2);
    text-decoration: underline;
  }

  /* Product name */
  .product-name-cell {
    max-width: 300px;
    cursor: default;
  }

  /* Spec chips */
  .spec-chip {
    font-size: 10px !important;
    height: 18px !important;
  }

  /* Stock indicator */
  .stock-indicator {
    display: inline-flex;
    align-items: center;
    padding: 4px 10px;
    border-radius: 20px;
    white-space: nowrap;
  }

  .stock--in {
    background-color: #e8f5e9;
  }
  .stock--in .stock-icon {
    color: #4caf50;
  }
  .stock--in .stock-label {
    color: #2e7d32;
  }

  .stock--out {
    background-color: #fce4ec;
  }
  .stock--out .stock-icon {
    color: #ef5350;
  }
  .stock--out .stock-label {
    color: #c62828;
  }

  .stock--notfound {
    background-color: #f5f5f5;
  }
  .stock--notfound .stock-icon {
    color: #9e9e9e;
  }
  .stock--notfound .stock-label {
    color: #757575;
  }

  /* Price cell */
  .price-cell {
    min-width: 100px;
  }

  /* Product image (unused but kept for reference) */
  .product-image {
    object-fit: contain;
    max-width: 80px;
    max-height: 60px;
  }

  /* Responsive */
  @media (max-width: 960px) {
    .product-name-cell {
      max-width: 180px;
    }
  }
</style>
